import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { Dropdown } from "react-bootstrap";
import { FiPlus, FiMoreHorizontal, FiSliders, FiLayers } from "react-icons/fi";
import { toastr } from "react-redux-toastr";
import { getSelectableEmployees } from "actions/employeesActions";
import * as expensesActions from "actions/expensesActions";
import { showModal, hideModal } from "actions/modalActions";
import { expensesService, expStatus } from "services";
import { errorFormating, isAdmin, isSupervisor } from "utils/utils";
import Loading from "components/Loading";
import ExpensePaymentModal from "components/Expenses/ExpensePaymentModal";
import ExpenseAnswerModal from "components/Expenses/ExpenseAnswerModal";
import MobileExpenseTabs from "./MobileExpenseTabs";
import MobileExpenseCard from "./MobileExpenseCard";
import MobileExpenseFiltersSheet from "./MobileExpenseFiltersSheet";
import "./MobileExpensesView.scss";

const PAGE_SIZE = 20;

const groupExpensesByDay = (items) => {
	const groups = new Map();
	items.forEach((expense) => {
		const dayKey = expense.day
			? moment(expense.day).format("YYYY-MM-DD")
			: "sin-fecha";
		if (!groups.has(dayKey)) groups.set(dayKey, []);
		groups.get(dayKey).push(expense);
	});
	return Array.from(groups.entries())
		.sort((a, b) => (a[0] < b[0] ? 1 : -1))
		.map(([day, records]) => ({ day, records }));
};

const formatSectionDate = (day, t) => {
	if (day === "sin-fecha") return t("expenses.mobile.noDate");
	const m = moment(day);
	const weekday = m.format("ddd").replace(/\./g, "");
	const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
	return `${cap} ${m.format("D MMM YYYY")}`;
};

const MobileExpensesView = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const user = useSelector((state) => state.authReducer?.user);
	const select_employees = useSelector(
		(state) => state.employeesReducer?.select_employees
	);
	const {
		expense_types,
		expense_groupings,
		expense_status,
		providers,
		payment_types,
	} = useSelector((state) => state.expensesReducer);

	const approveMode = location.pathname === "/pending-expenses";
	const approvedMode = location.pathname === "/approved-expenses";
	const canSupervise = isAdmin() || isSupervisor();

	const [filtersOpen, setFiltersOpen] = useState(false);

	const [filterEmployeeId, setFilterEmployeeId] = useState("");
	const [filterEmployeeName, setFilterEmployeeName] = useState("");
	const [filterProviderId, setFilterProviderId] = useState("");
	const [filterExpenseTypeId, setFilterExpenseTypeId] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const [items, setItems] = useState([]);
	const [page, setPage] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);

	const closeModal = useCallback(() => dispatch(hideModal()), [dispatch]);

	const buildOptions = useCallback(
		(pageNum) => {
			const base = {
				provider_id: filterProviderId || undefined,
				expense_type_id: filterExpenseTypeId || undefined,
				date_from: startDate || undefined,
				date_to: endDate || undefined,
				page: pageNum,
				page_size: PAGE_SIZE,
			};
			if (approveMode) {
				return {
					...base,
					status: expStatus.pending,
					...(canSupervise && filterEmployeeId
						? { employee_id: filterEmployeeId }
						: {}),
				};
			}
			if (approvedMode) {
				return {
					...base,
					status: expStatus.approved,
					...(canSupervise && filterEmployeeId
						? { employee_id: filterEmployeeId }
						: {}),
				};
			}
			return {
				...base,
				...(filterStatus ? { status: filterStatus } : {}),
				...(canSupervise && filterEmployeeId
					? { employee_id: filterEmployeeId }
					: {}),
				...(!canSupervise && user?.id ? { employee_id: user.id } : {}),
			};
		},
		[
			approveMode,
			approvedMode,
			canSupervise,
			filterEmployeeId,
			filterProviderId,
			filterExpenseTypeId,
			filterStatus,
			startDate,
			endDate,
			user?.id,
		]
	);

	const computeHasMore = useCallback((mergedLength, chunkLen, totalItems) => {
		if (chunkLen === 0) return false;
		if (totalItems > 0) return mergedLength < totalItems;
		return chunkLen === PAGE_SIZE;
	}, []);

	const loadFirstPage = useCallback(async () => {
		setLoading(true);
		setLoadingMore(false);
		try {
			const response = await expensesService.getAll(buildOptions(0));
			const chunk = response.data || [];
			const totalItems =
				response.pagination?.total_items ?? response.total ?? 0;
			setItems(chunk);
			setPage(0);
			setHasMore(computeHasMore(chunk.length, chunk.length, totalItems));
		} catch (e) {
			console.error("Error cargando gastos (mobile):", e);
			setItems([]);
			setHasMore(false);
		} finally {
			setLoading(false);
		}
	}, [buildOptions, computeHasMore]);

	const fetchMoreData = useCallback(async () => {
		if (!hasMore || loadingMore || loading) return;
		const nextPage = page + 1;
		setLoadingMore(true);
		try {
			const response = await expensesService.getAll(buildOptions(nextPage));
			const chunk = response.data || [];
			const totalItems =
				response.pagination?.total_items ?? response.total ?? 0;
			setItems((prev) => {
				const merged = prev.concat(chunk);
				setHasMore(computeHasMore(merged.length, chunk.length, totalItems));
				return merged;
			});
			setPage(nextPage);
		} catch (e) {
			console.error("Error cargando más gastos (mobile):", e);
			setHasMore(false);
		} finally {
			setLoadingMore(false);
		}
	}, [
		hasMore,
		loadingMore,
		loading,
		page,
		buildOptions,
		computeHasMore,
	]);

	useEffect(() => {
		dispatch(expensesActions.getAllExpenseTypes());
		dispatch(expensesActions.getAllExpenseGroupings());
		dispatch(expensesActions.getAllPaymentTypes());
		dispatch(expensesActions.getAllProviders());
		dispatch(expensesActions.getAllExpenseStatus());
		dispatch(getSelectableEmployees());
	}, [dispatch]);

	useEffect(() => {
		loadFirstPage();
	}, [
		location.pathname,
		filterEmployeeId,
		filterProviderId,
		filterExpenseTypeId,
		filterStatus,
		startDate,
		endDate,
		user?.id,
		loadFirstPage,
	]);

	useEffect(() => {
		if (!location.state?.refreshExpenses) return;
		loadFirstPage();
		history.replace(location.pathname + location.search, {});
	}, [location.state?.refreshExpenses, loadFirstPage, history, location.pathname, location.search]);

	const groupedList = useMemo(() => groupExpensesByDay(items), [items]);

	const openAlertModal = useCallback(
		(title, message) => {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title,
						style: { width: "400px" },
						message,
						closeModal,
					},
					modalType: "alert",
				})
			);
		},
		[dispatch, closeModal]
	);

	const openEditExpenseModal = useCallback(
		(expense) => {
			const disabled = expense.status !== expStatus.pending;
			history.push(`/expenses/${expense.id}`, {
				expense,
				disabled,
				returnTo: location.pathname,
			});
		},
		[history, location.pathname]
	);

	const openNewExpenseModal = useCallback(() => {
		history.push("/expenses/new", { returnTo: location.pathname });
	}, [history, location.pathname]);

	const openPaymentModal = useCallback(
		(expense) => {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("expenses.addPayment"),
						style: { width: "500px" },
						content: (
							<ExpensePaymentModal
								expense={expense}
								employees={select_employees ?? []}
								currentUser={user}
								getExpenses={loadFirstPage}
								handleClose={closeModal}
							/>
						),
						closeModal,
					},
					modalType: "edit",
				})
			);
		},
		[dispatch, closeModal, loadFirstPage, select_employees, user, t]
	);

	const confirmCancel = useCallback(
		async (expense) => {
			const updated = {
				id: expense.id,
				day: expense.day,
				value: expense.value,
				type_id: expense.type_id ?? expense.type?.id,
				provider_id: expense.provider_id ?? expense.provider?.id,
				comments: expense.comments,
				answer_comments: expense.answer_comments,
				payer_comments: expense.payer_comments,
				status: expStatus.canceled,
			};
			const response = await expensesService.update(updated);
			closeModal();

			if (!response.ok) {
				const textError = errorFormating(response);
				openAlertModal(t("expenses.errorCancel"), textError.general);
			} else {
				loadFirstPage();
			}
		},
		[closeModal, loadFirstPage, openAlertModal, t]
	);

	const openCancelExpenseModal = useCallback(
		(expense) => {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("expenses.cancelExpense"),
						style: { width: "400px" },
						message: t("expenses.cancelConfirm"),
						deleteAction: () => confirmCancel(expense),
						closeModal,
					},
					modalType: "delete",
				})
			);
		},
		[dispatch, closeModal, confirmCancel, t]
	);

	const handleExpenseStatus = useCallback(
		async (expense, isApproved) => {
			const updated = {
				id: expense.id,
				day: expense.day,
				value: expense.value,
				type_id: expense.type_id ?? expense.type?.id,
				provider_id: expense.provider?.id ?? expense.provider_id,
				comments: expense.comments,
				answer_comments: expense.answer_comments,
				payer_comments: expense.payer_comments,
				status: isApproved ? expStatus.approved : expStatus.denied,
			};
			const response = await expensesService.update(updated);

			if (response.ok) {
				toastr.success(
					t("common.success"),
					isApproved ? t("expenses.expenseApproved") : t("expenses.expenseDenied")
				);
				loadFirstPage();
			} else {
				const msg = response.errors?.[0]?.description || t("common.errorGeneric");
				openAlertModal(
					msg,
					isApproved ? t("expenses.errorApprove") : t("expenses.errorDeny")
				);
			}
		},
		[loadFirstPage, openAlertModal, t]
	);

	const confirmDisapprove = useCallback(
		async (expense) => {
			const updated = {
				id: expense.id,
				day: expense.day,
				value: expense.value,
				type_id: expense.type_id ?? expense.type?.id,
				provider_id: expense.provider_id ?? expense.provider?.id,
				comments: expense.comments,
				answer_comments: expense.answer_comments,
				payer_comments: expense.payer_comments,
				status: expStatus.pending,
			};
			const response = await expensesService.update(updated);
			closeModal();

			if (!response.ok) {
				const msg = response.errors?.[0]?.description || t("common.errorGeneric");
				openAlertModal(t("expenses.errorDisapprove"), msg);
			} else {
				toastr.success(t("common.success"), t("expenses.expenseDisapproved"));
				loadFirstPage();
			}
		},
		[closeModal, loadFirstPage, openAlertModal, t]
	);

	const openDisapproveExpenseModal = useCallback(
		(expense) => {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("expenses.disapproveExpense"),
						style: { width: "400px" },
						message: t("expenses.disapproveConfirm"),
						deleteText: t("expenses.disapprove"),
						loadingText: t("expenses.disapproving"),
						deleteAction: () => confirmDisapprove(expense),
						closeModal,
					},
					modalType: "delete",
				})
			);
		},
		[dispatch, closeModal, confirmDisapprove, t]
	);

	const openAnswerModal = useCallback(
		(expense) => {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("expenses.approveOrDenyExpense"),
						style: { width: "500px", maxWidth: "calc(100vw - 32px)" },
						content: (
							<ExpenseAnswerModal
								expense={expense}
								expenseTypes={expense_types}
								getExpenses={loadFirstPage}
								handleClose={closeModal}
							/>
						),
						closeModal,
					},
					modalType: "edit",
				})
			);
		},
		[dispatch, closeModal, loadFirstPage, expense_types, t]
	);

	const downloadAttachment = useCallback(async (expense) => {
		const attachment = expense.attachments[0];
		const response = await expensesService.getAttachment(
			expense.id,
			attachment.url
		);
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		document.body.appendChild(a);
		a.href = url;
		a.download = attachment.name;
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	}, []);

	const onStartDateNativeChange = useCallback((value) => {
		setStartDate(value);
	}, []);

	const onEndDateNativeChange = useCallback((value) => {
		setEndDate(value);
	}, []);

	const clearFilters = useCallback(() => {
		setFilterEmployeeId("");
		setFilterEmployeeName("");
		setFilterProviderId("");
		setFilterExpenseTypeId("");
		setFilterStatus("");
		setStartDate(null);
		setEndDate(null);
	}, []);

	const activeFilterCount = [
		canSupervise && filterEmployeeId,
		filterProviderId,
		filterExpenseTypeId,
		!approveMode && !approvedMode && filterStatus,
		startDate || endDate,
	].filter(Boolean).length;

	const screenTitle = approveMode
		? t("expenses.mobile.approveTitle")
		: approvedMode
			? t("expenses.mobile.approvedTitle")
			: canSupervise
				? t("expenses.mobile.adminTitle")
				: t("expenses.mobile.myTitle");

	return (
		<div className="mobile-expenses-view">
			<div className="mobile-expenses-view__heading">
				<h1 className="mobile-expenses-view__title">{screenTitle}</h1>
			</div>

			<MobileExpenseTabs />

			<div className="mobile-expenses-view__toolbar">
				<div className="mobile-expenses-view__toolbar-spacer" />
				{!approveMode && !approvedMode && (
					<button
						type="button"
						className="mobile-expenses-view__fab"
						title={t("expenses.newExpense")}
						onClick={openNewExpenseModal}
					>
						<FiPlus size={22} />
					</button>
				)}
			</div>

			<div className="mobile-expenses-view__body">
				{loading && items.length === 0 ? (
					<div className="mobile-expenses-view__loading">
						<Loading />
					</div>
				) : items.length === 0 ? (
					<div className="mobile-expenses-view__empty">
						{approveMode
							? t("expenses.mobile.emptyApprove")
							: approvedMode
								? t("expenses.mobile.emptyApproved")
								: t("expenses.mobile.emptyPeriod")}
					</div>
				) : (
					<InfiniteScroll
						dataLength={items.length}
						next={fetchMoreData}
						hasMore={hasMore}
						scrollThreshold={0.9}
						loader={
							loadingMore ? (
								<div className="mobile-expenses-view__load-more">
									{t("expenses.mobile.loadingMore")}
								</div>
							) : null
						}
						endMessage={
							<div className="mobile-expenses-view__end-message">
								{t("common.noMoreResults")}
							</div>
						}
						style={{ overflow: "visible" }}
					>
						{groupedList.map(({ day, records }) => (
							<div key={day} className="mobile-expenses-view__section">
								<div className="mobile-expenses-view__section-title">
									{formatSectionDate(day, t)}
								</div>
								<div className="mobile-expenses-view__section-list">
									{records.map((expense) => (
										<MobileExpenseCard
											key={expense.id}
											expense={expense}
											mode={approveMode ? "approve" : "list"}
											expense_types={expense_types}
											expense_groupings={expense_groupings}
											onOpenDetail={openEditExpenseModal}
											onAddPayment={
												canSupervise ? openPaymentModal : undefined
											}
											onCancelExpense={openCancelExpenseModal}
											onDisapproveExpense={
												approvedMode ? openDisapproveExpenseModal : undefined
											}
											onApproveQuick={(ex) => handleExpenseStatus(ex, true)}
											onDenyQuick={(ex) => handleExpenseStatus(ex, false)}
											onCommentModal={openAnswerModal}
											onDownloadAttachment={downloadAttachment}
										/>
									))}
								</div>
							</div>
						))}
					</InfiniteScroll>
				)}
			</div>

			<div className="mobile-expenses-view__bottom-bar">
				<button
					type="button"
					className={`mobile-expenses-view__filters-btn${activeFilterCount > 0 ? " mobile-expenses-view__filters-btn--active" : ""}`}
					onClick={() => setFiltersOpen(true)}
				>
					<FiSliders size={18} />
					{t("expenses.mobile.filters.title")}
					{activeFilterCount > 0 && (
						<span className="mobile-expenses-view__filters-badge">
							{activeFilterCount}
						</span>
					)}
				</button>
			</div>

			<MobileExpenseFiltersSheet
				open={filtersOpen}
				onClose={() => setFiltersOpen(false)}
				approveMode={approveMode || approvedMode}
				canSupervise={canSupervise}
				filterEmployeeId={filterEmployeeId}
				filterEmployeeName={filterEmployeeName}
				onEmployeeChange={(id, emp) => {
					setFilterEmployeeId(id || "");
					setFilterEmployeeName(
						emp
							? `${emp.name ?? ""} ${emp.surname ?? ""}`.trim()
							: ""
					);
				}}
				filterProviderId={filterProviderId}
				filterExpenseTypeId={filterExpenseTypeId}
				filterStatus={filterStatus}
				startDate={startDate}
				endDate={endDate}
				onStartDateChange={onStartDateNativeChange}
				onEndDateChange={onEndDateNativeChange}
				onFilterProviderChange={(e) =>
					setFilterProviderId(e.target.value)
				}
				onFilterExpenseTypeChange={(e) =>
					setFilterExpenseTypeId(e.target.value)
				}
				onFilterStatusChange={(e) => setFilterStatus(e.target.value)}
				providers={providers}
				expense_types={expense_types}
				expense_status={expense_status}
				onClearFilters={clearFilters}
			/>
		</div>
	);
};

export default MobileExpensesView;
